import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const AdminSecureLoginPortal = () => {
  const [email, setEmail] = useState('admin@obasegroup.com');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutTimer, setLockoutTimer] = useState(0);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLocked && lockoutTimer > 0) {
      const timer = setTimeout(() => {
        setLockoutTimer(lockoutTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (isLocked && lockoutTimer === 0) {
      setIsLocked(false);
      setLoginAttempts(0);
      setError('');
      setErrorCode('');
    }
  }, [isLocked, lockoutTimer]);

  const classifyError = (msg = '', status) => {
    const lower = msg?.toLowerCase();
    if (
      lower?.includes('database error querying schema') ||
      lower?.includes('sql: scan error') ||
      lower?.includes('converting null to string') ||
      lower?.includes('confirmation_token') ||
      status === 500
    ) {
      return 'db_null_token';
    }
    if (lower?.includes('invalid login credentials') || lower?.includes('invalid credentials')) {
      return 'invalid_credentials';
    }
    if (lower?.includes('email not confirmed')) {
      return 'email_not_confirmed';
    }
    if (lower?.includes('too many requests') || lower?.includes('rate limit')) {
      return 'rate_limited';
    }
    if (lower?.includes('network') || lower?.includes('fetch') || lower?.includes('failed to fetch')) {
      return 'network_error';
    }
    return 'unknown';
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (isLocked) {
      setError(`Account temporarily locked. Please wait ${lockoutTimer} seconds.`);
      return;
    }

    setError('');
    setErrorCode('');
    setShowDiagnostics(false);
    setLoading(true);

    try {
      await signIn(email, password);
      setLoginAttempts(0);
      navigate('/admin-control-panel');
    } catch (err) {
      const msg = err?.message || '';
      const status = err?.status;
      const code = classifyError(msg, status);
      setErrorCode(code);

      if (code === 'db_null_token') {
        setError('Database schema error: NULL token field detected in auth.users. The migration fix may not have been applied yet.');
        setShowDiagnostics(true);
      } else if (code === 'invalid_credentials') {
        setError('Invalid email or password. Please check your credentials and try again.');
      } else if (code === 'email_not_confirmed') {
        setError('Email not confirmed. Please contact your administrator.');
      } else if (code === 'rate_limited') {
        setError('Too many login attempts. Please wait a few minutes before trying again.');
      } else if (code === 'network_error') {
        setError('Network error — unable to reach Supabase. Check your internet connection.');
      } else {
        setError(msg || 'Login failed. Please try again.');
      }

      handleFailedAttempt();
    } finally {
      setLoading(false);
    }
  };

  const handleFailedAttempt = () => {
    const newAttempts = loginAttempts + 1;
    setLoginAttempts(newAttempts);

    if (newAttempts >= 5) {
      setIsLocked(true);
      setLockoutTimer(300);
      setError('Too many failed attempts. Account locked for 5 minutes.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-red-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-red-500/30">
            <Icon name="ShieldCheck" size={40} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Admin Login</h1>
          <p className="text-sm text-slate-400">OBase Group — Control Panel Access</p>
        </div>

        {/* Login Card */}
        <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl shadow-2xl border border-slate-700 p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Admin Email Address
              </label>
              <div className="relative">
                <Icon name="Mail" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  placeholder="admin@obasegroup.com"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  disabled={isLocked}
                  required
                  className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Password
              </label>
              <div className="relative">
                <Icon name="Lock" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="password"
                  placeholder="Enter admin password"
                  value={password}
                  onChange={(e) => setPassword(e?.target?.value)}
                  disabled={isLocked}
                  required
                  className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="space-y-3">
                <div className={`border p-4 rounded-lg text-sm flex items-start gap-3 ${
                  errorCode === 'db_null_token' ?'bg-orange-500/10 border-orange-500/30 text-orange-300' :'bg-red-500/10 border-red-500/30 text-red-300'
                }`}>
                  <Icon
                    name={errorCode === 'db_null_token' ? 'Database' : 'AlertTriangle'}
                    size={18}
                    className="flex-shrink-0 mt-0.5"
                  />
                  <div className="flex-1">
                    <p className="font-semibold mb-1">
                      {errorCode === 'db_null_token' ? 'Database Schema Error (500)' : 'Authentication Failed'}
                    </p>
                    <p className="text-xs opacity-90">{error}</p>
                  </div>
                </div>

                {/* Detailed diagnostics for NULL token error */}
                {showDiagnostics && errorCode === 'db_null_token' && (
                  <div className="bg-slate-900/70 border border-orange-500/20 rounded-lg p-4 text-xs text-slate-300 space-y-3">
                    <div className="flex items-center gap-2 text-orange-300 font-semibold">
                      <Icon name="Wrench" size={14} />
                      <span>Troubleshooting Guide</span>
                    </div>

                    <div>
                      <p className="text-orange-200 font-medium mb-1">Root Cause</p>
                      <p className="text-slate-400 leading-relaxed">
                        Supabase GoTrue crashed while scanning <code className="bg-slate-800 px-1 rounded text-orange-300">auth.users.confirmation_token</code> — the column contains a <code className="bg-slate-800 px-1 rounded text-orange-300">NULL</code> value which Go cannot convert to a non-nullable string.
                      </p>
                    </div>

                    <div>
                      <p className="text-orange-200 font-medium mb-1">Fix Steps (in order)</p>
                      <ol className="space-y-1.5 text-slate-400 list-decimal list-inside">
                        <li>Go to <span className="text-white">Supabase Dashboard → SQL Editor</span></li>
                        <li>Run the migration: <code className="bg-slate-800 px-1 rounded text-green-300">20260408182000_fix_null_confirmation_token.sql</code></li>
                        <li>Verify with: <code className="bg-slate-800 px-1 rounded text-blue-300">SELECT id, email, confirmation_token FROM auth.users WHERE confirmation_token IS NULL;</code></li>
                        <li>If rows still show NULL, run: <code className="bg-slate-800 px-1 rounded text-blue-300">UPDATE auth.users SET confirmation_token = '' WHERE confirmation_token IS NULL;</code></li>
                        <li>Retry login after applying the fix</li>
                      </ol>
                    </div>

                    <div>
                      <p className="text-orange-200 font-medium mb-1">Also check these columns</p>
                      <div className="flex flex-wrap gap-1">
                        {['recovery_token', 'email_change_token_new', 'email_change', 'reauthentication_token']?.map(col => (
                          <code key={col} className="bg-slate-800 px-1.5 py-0.5 rounded text-slate-300">{col}</code>
                        ))}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowDiagnostics(false)}
                      className="text-slate-500 hover:text-slate-300 text-xs underline"
                    >
                      Hide diagnostics
                    </button>
                  </div>
                )}

                {/* Show diagnostics toggle for non-db errors */}
                {!showDiagnostics && errorCode !== 'db_null_token' && loginAttempts >= 2 && (
                  <button
                    type="button"
                    onClick={() => setShowDiagnostics(true)}
                    className="text-xs text-slate-400 hover:text-slate-200 underline flex items-center gap-1"
                  >
                    <Icon name="HelpCircle" size={12} />
                    Need help troubleshooting?
                  </button>
                )}

                {/* General troubleshooting tips */}
                {showDiagnostics && errorCode !== 'db_null_token' && (
                  <div className="bg-slate-900/70 border border-slate-600 rounded-lg p-4 text-xs text-slate-300 space-y-2">
                    <div className="flex items-center gap-2 text-blue-300 font-semibold">
                      <Icon name="HelpCircle" size={14} />
                      <span>Troubleshooting Tips</span>
                    </div>
                    <ul className="space-y-1 text-slate-400">
                      <li>• Ensure you are using the correct admin email: <span className="text-white">admin@obasegroup.com</span></li>
                      <li>• Password is case-sensitive — check Caps Lock</li>
                      <li>• If password was recently changed, use the new password</li>
                      <li>• Check Supabase Dashboard → Authentication → Users to verify the account exists and is confirmed</li>
                      <li>• If the account shows "Unconfirmed", run: <code className="bg-slate-800 px-1 rounded text-green-300">UPDATE auth.users SET email_confirmed_at = NOW() WHERE email = 'admin@obasegroup.com';</code></li>
                    </ul>
                    <button
                      type="button"
                      onClick={() => setShowDiagnostics(false)}
                      className="text-slate-500 hover:text-slate-300 text-xs underline"
                    >
                      Hide tips
                    </button>
                  </div>
                )}
              </div>
            )}

            {loginAttempts > 0 && !isLocked && !error && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 p-3 rounded-lg text-xs flex items-center gap-2">
                <Icon name="AlertCircle" size={16} />
                <span>Failed attempts: {loginAttempts}/5</span>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white py-3 rounded-lg font-semibold shadow-lg shadow-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || isLocked}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Icon name="Loader2" size={20} className="animate-spin" />
                  Authenticating...
                </span>
              ) : isLocked ? (
                <span className="flex items-center justify-center gap-2">
                  <Icon name="Lock" size={20} />
                  Locked ({lockoutTimer}s)
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Icon name="ShieldCheck" size={20} />
                  Sign In to Admin Panel
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-5 border-t border-slate-700">
            <div className="bg-slate-900/50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={16} className="text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-slate-400">
                  <p className="font-medium mb-1 text-blue-300">Security Notice</p>
                  <ul className="space-y-1">
                    <li>• All login attempts are logged and monitored</li>
                    <li>• Account lockout after 5 failed attempts</li>
                    <li>• Session timeout: 30 minutes of inactivity</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5 text-center">
            <Link to="/service-catalog-home" className="text-sm text-slate-400 hover:text-white transition-colors">
              ← Return to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSecureLoginPortal;